import {
  formatIsoLabel,
  isoWeekEndDate,
  isoWeekStartDate,
  normalizeIsoWeekNumber,
  normalizeIsoYear,
} from '~/utils/isoWeek';

export const WORKOUT_SCHEDULE_DAY_UNKNOWN_HINT =
  'Задайте дни в программе или укажите дату вручную';

export const SCHEDULE_WEEK_UNDETERMINED_DAY_LABEL = 'День не определён';
const LEGACY_SCHEDULE_WEEK_UNDETERMINED_DAY_LABELS = [
  'День не определен',
  'Дата не определена',
];
export type WorkoutScheduleContext = {
  calendar_day_known?: boolean | null;
  iso_year?: number | null;
  iso_week?: number | null;
  iso_week_label?: string | null;
  schedule_day_hint?: string | null;
  schedule_mode?: string | null;
  schedule_assignment_mode?: string | null;
  effective_date?: string | null;
  relative_day_index?: number | null;
  relative_day_number?: number | null;
  relative_day_label?: string | null;
};

export function isWorkoutCalendarDayKnown(
  scheduleContext?: WorkoutScheduleContext | null,
): boolean {
  if (!scheduleContext || typeof scheduleContext !== 'object') {
    return true;
  }
  return scheduleContext.calendar_day_known !== false;
}

export function isWorkoutFullyUndeterminedScheduleDay(
  scheduleContext?: WorkoutScheduleContext | null,
  scheduleDateLabel = '',
): boolean {
  if (resolveWorkoutRelativeDayNumber(scheduleContext) !== null) {
    return false;
  }

  if (!isWorkoutCalendarDayKnown(scheduleContext)) {
    return true;
  }

  const label = String(scheduleDateLabel ?? '').trim();
  if (!label) {
    return false;
  }

  if (
    label === SCHEDULE_WEEK_UNDETERMINED_DAY_LABEL
    || LEGACY_SCHEDULE_WEEK_UNDETERMINED_DAY_LABELS.includes(label)
  ) {
    return true;
  }

  return (
    label.startsWith(`${SCHEDULE_WEEK_UNDETERMINED_DAY_LABEL}.`)
    || LEGACY_SCHEDULE_WEEK_UNDETERMINED_DAY_LABELS.some(
      (legacyLabel) => label.startsWith(`${legacyLabel}.`),
    )
  );
}

export function resolveWorkoutFullyUndeterminedDayHint(
  scheduleContext?: WorkoutScheduleContext | null,
  hint = '',
): string {
  const resolved = String(
    hint || scheduleContext?.schedule_day_hint || WORKOUT_SCHEDULE_DAY_UNKNOWN_HINT,
  ).trim();

  return resolved || WORKOUT_SCHEDULE_DAY_UNKNOWN_HINT;
}

function formatShortRussianDate(ymd: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!match) {
    return '';
  }
  const month = Number(match[2]);
  const day = Number(match[3]);
  const months = [
    'янв', 'фев', 'мар', 'апр', 'мая', 'июн',
    'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
  ];
  return `${day} ${months[month - 1] ?? match[2]}`;
}

export function formatWorkoutIsoWeekLabel(
  scheduleContext?: WorkoutScheduleContext | null,
): string {
  const fromApi = String(scheduleContext?.iso_week_label ?? '').trim();
  if (fromApi) {
    return fromApi;
  }

  const isoYear = normalizeIsoYear(scheduleContext?.iso_year);
  const isoWeek = normalizeIsoWeekNumber(scheduleContext?.iso_week);
  if (isoYear === null || isoWeek === null) {
    return '';
  }

  const start = formatShortRussianDate(isoWeekStartDate(isoYear, isoWeek));
  const end = formatShortRussianDate(isoWeekEndDate(isoYear, isoWeek));
  if (!start || !end) {
    return formatIsoLabel(isoYear, isoWeek);
  }

  return `Неделя ${isoWeek} (${start} – ${end})`;
}

/** Диапазон ISO-недели без номера: «6 июл – 12 июл». */
export function formatWorkoutIsoWeekRangeLabel(
  scheduleContext?: WorkoutScheduleContext | null,
): string {
  const isoYear = normalizeIsoYear(scheduleContext?.iso_year);
  const isoWeek = normalizeIsoWeekNumber(scheduleContext?.iso_week);
  if (isoYear !== null && isoWeek !== null) {
    const start = formatShortRussianDate(isoWeekStartDate(isoYear, isoWeek));
    const end = formatShortRussianDate(isoWeekEndDate(isoYear, isoWeek));
    if (start && end) {
      return `${start} – ${end}`;
    }
  }

  const fromApi = String(scheduleContext?.iso_week_label ?? '').trim();
  if (!fromApi) {
    return '';
  }

  const fromWeekLabel = /^Неделя\s+\d+\s*\((.+)\)\s*$/.exec(fromApi);
  if (fromWeekLabel?.[1]) {
    return fromWeekLabel[1].trim();
  }

  if (fromApi.includes(' – ') && !/^Неделя\s+\d+/i.test(fromApi)) {
    return fromApi;
  }

  return '';
}

export function formatWorkoutScheduleDayUnknownLabel(
  scheduleContext?: WorkoutScheduleContext | null,
): string {
  const weekLabel = formatWorkoutIsoWeekLabel(scheduleContext);
  const hint = String(
    scheduleContext?.schedule_day_hint ?? WORKOUT_SCHEDULE_DAY_UNKNOWN_HINT,
  ).trim();

  if (weekLabel && hint) {
    return `${weekLabel}. ${hint}`;
  }
  if (weekLabel) {
    return weekLabel;
  }
  return hint;
}

export function resolveWorkoutRelativeDayNumber(
  scheduleContext?: WorkoutScheduleContext | null,
): number | null {
  const fromIndex = Number(scheduleContext?.relative_day_index);
  if (Number.isFinite(fromIndex) && fromIndex > 0) {
    return Math.floor(fromIndex);
  }

  const value = Number(scheduleContext?.relative_day_number);
  if (Number.isFinite(value) && value > 0) {
    return Math.floor(value);
  }
  return null;
}

export function formatWorkoutRelativeDayLabel(
  scheduleContext?: WorkoutScheduleContext | null,
): string {
  const fromApi = String(scheduleContext?.relative_day_label ?? '').trim();
  if (fromApi) {
    return fromApi;
  }

  const dayNumber = resolveWorkoutRelativeDayNumber(scheduleContext);
  return dayNumber !== null ? `День ${dayNumber}` : SCHEDULE_WEEK_UNDETERMINED_DAY_LABEL;
}
